import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { User, LoginRequest, LoginResponse, Permission } from '@/types/user.types';
import { api } from '@/api/client';
import { entityConfigKeys } from '@/api/hooks/useEntityConfig';
import i18n from '@/i18n';

const LANGUAGE_STORAGE_KEY = 'activeid-language';

/**
 * Apply the per-user language preference returned by the backend. The backend
 * is the source of truth across devices, so this overrides the local default.
 * No-op when the user has no stored preference.
 */
function applyPreferredLanguage(pref?: string | null) {
  if (!pref) return;
  const lang = pref.toLowerCase().startsWith('en') ? 'en' : 'es';
  if (i18n.language !== lang) {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    i18n.changeLanguage(lang);
  }
}

interface MfaState {
  required: boolean;
  isSetup: boolean;
}

interface LoginResult {
  mfaRequired: boolean;
  isMfaSetup: boolean;
}

interface MfaQrCodeResult {
  qrCode: string;
  isMfaSetup: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  mfaState: MfaState;
  login: (credentials: LoginRequest) => Promise<LoginResult>;
  logout: () => void;
  validateMfa: (code: string) => Promise<void>;
  getMfaQrCode: () => Promise<MfaQrCodeResult>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = 'activeid_token';
const USER_KEY = 'activeid_user';

/**
 * Feature 4: parse the user's allowed location ids from the /auth/me or
 * /auth/login response. Returns null when the user is unrestricted, or an
 * array of ids otherwise. Accepts several shapes because the backend uses
 * different fields in different responses:
 *   - allowedLocationIds: number[]       → restricted set
 *   - allowedLocationIds: null           → unrestricted
 *   - isUnrestricted: true               → unrestricted
 *   - (field missing entirely)           → unrestricted (rollout safety)
 */
function parseAllowedLocationIds(raw: Record<string, unknown>): number[] | null {
  if (raw['isUnrestricted'] === true) return null;
  const ids = raw['allowedLocationIds'];
  if (ids === null) return null;
  if (Array.isArray(ids)) {
    return ids
      .map((v) => (typeof v === 'number' ? v : Number(v)))
      .filter((n) => Number.isFinite(n));
  }
  // Missing field on authenticated response ⇒ treat as unrestricted, matching
  // the backend's rollout-safety behavior in CurrentUserContext.
  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mfaState, setMfaState] = useState<MfaState>({ required: false, isSetup: false });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Initialize auth state from localStorage, then refresh from API for fresh permissions
  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const storedUser = localStorage.getItem(USER_KEY);

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));

      // Fetch fresh user data with permissions from API
      // Backend returns ReactUserMe directly (flat object, not wrapped)
      api.get<Record<string, unknown>>('/api/auth/me')
        .then((response) => {
          const me = response.data;
          if (me && (me.id || me.userName)) {
            const roleStr = String(me.role ?? '');
            const allowed = parseAllowedLocationIds(me);
            const freshUser: User = {
              id: Number(me.id),
              userName: String(me.userName ?? ''),
              name: String(me.name ?? me.userName ?? ''),
              role: roleStr,
              entity: String(me.entity ?? ''),
              clientId: me.clientId ? Number(me.clientId) : undefined,
              clientName: me.clientName ? String(me.clientName) : undefined,
              isAdmin: roleStr === 'Admin',
              isActive: true,
              mfaEnabled: false,
              permissions: (me.permissions as Permission[]) ?? [],
              roles: (me.roles as string[]) ?? [],
              allowedLocationIds: allowed,
              isUnrestricted: roleStr === 'Admin' || allowed === null,
              preferredLanguage: me.preferredLanguage ? String(me.preferredLanguage) : undefined,
            };
            setUser(freshUser);
            localStorage.setItem(USER_KEY, JSON.stringify(freshUser));
            applyPreferredLanguage(freshUser.preferredLanguage);
          }
        })
        .catch(() => {
          // Token may be expired — clear auth state
          setUser(null);
          setToken(null);
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
        })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = useCallback(async (credentials: LoginRequest): Promise<LoginResult> => {
    let response;
    try {
      response = await api.post<LoginResponse>('/api/auth/login', {
        ...credentials,
        timezoneOffset: new Date().getTimezoneOffset(),
      });
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.data?.message) {
        throw new Error(err.response.data.message);
      }
      throw new Error('Unable to connect to the server.');
    }

    if (!response.data.success || !response.data.serviceToken) {
      throw new Error(response.data.message || 'Login failed');
    }

    const { serviceToken, user: userData, mfaRequired, isMfaSetup } = response.data;

    if (mfaRequired) {
      setToken(serviceToken);
      setUser(userData);
      setMfaState({ required: true, isSetup: isMfaSetup ?? false });
      localStorage.setItem(TOKEN_KEY, serviceToken);
      return { mfaRequired: true, isMfaSetup: isMfaSetup ?? false };
    }

    const allowed = parseAllowedLocationIds({
      ...(userData as unknown as Record<string, unknown>),
      ...(response.data as unknown as Record<string, unknown>),
    });
    const fullUser: User = {
      ...userData,
      isAdmin: userData.isAdmin || userData.role === 'Admin',
      permissions: response.data.permissions ?? userData.permissions ?? [],
      roles: response.data.roles ?? userData.roles ?? [],
      allowedLocationIds: allowed,
      isUnrestricted: (userData.isAdmin || userData.role === 'Admin') || allowed === null,
    };
    setToken(serviceToken);
    setUser(fullUser);
    setMfaState({ required: false, isSetup: false });

    localStorage.setItem(TOKEN_KEY, serviceToken);
    localStorage.setItem(USER_KEY, JSON.stringify(fullUser));
    applyPreferredLanguage(fullUser.preferredLanguage);

    // A fresh login may be a different tenant than whatever is still cached
    // from a prior session in this browser — force a refetch of the tenant
    // settings that gate feature flags and branding.
    queryClient.removeQueries({ queryKey: entityConfigKeys.systemSettings() });

    return { mfaRequired: false, isMfaSetup: false };
  }, [queryClient]);

  const getMfaQrCode = useCallback(async (): Promise<MfaQrCodeResult> => {
    const response = await api.get<{ success: boolean; qrCode: string; isMfaSetup: boolean }>(
      '/api/auth/mfa/qrcode'
    );

    if (response.data.success) {
      return {
        qrCode: response.data.qrCode,
        isMfaSetup: response.data.isMfaSetup,
      };
    }

    throw new Error('Failed to get QR code');
  }, []);

  const validateMfa = useCallback(async (code: string): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await api.post<LoginResponse>('/api/auth/mfa/validate', { code });

      if (response.data.success && response.data.serviceToken) {
        const { serviceToken, user: userData } = response.data;

        // Update with full access token + map permissions/roles
        const allowed = parseAllowedLocationIds({
          ...(userData as unknown as Record<string, unknown>),
          ...(response.data as unknown as Record<string, unknown>),
        });
        const fullUser: User = {
          ...userData,
          isAdmin: userData.isAdmin || userData.role === 'Admin',
          permissions: response.data.permissions ?? userData.permissions ?? [],
          roles: response.data.roles ?? userData.roles ?? [],
          allowedLocationIds: allowed,
          isUnrestricted: (userData.isAdmin || userData.role === 'Admin') || allowed === null,
        };
        setToken(serviceToken);
        setUser(fullUser);
        setMfaState({ required: false, isSetup: true });

        // Persist to localStorage
        localStorage.setItem(TOKEN_KEY, serviceToken);
        localStorage.setItem(USER_KEY, JSON.stringify(fullUser));
        applyPreferredLanguage(fullUser.preferredLanguage);

        // Same tenant-switch safety as the non-MFA login path.
        queryClient.removeQueries({ queryKey: entityConfigKeys.systemSettings() });
      } else {
        throw new Error(response.data.message || 'MFA validation failed');
      }
    } finally {
      setIsLoading(false);
    }
  }, [queryClient]);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    setMfaState({ required: false, isSetup: false });
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    // Drop all cached tenant data so the next login (possibly a different
    // tenant in the same browser) never sees the previous tenant's flags,
    // branding, or lists.
    queryClient.clear();
    navigate('/login');
  }, [navigate, queryClient]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token && !mfaState.required,
        isLoading,
        mfaState,
        login,
        logout,
        validateMfa,
        getMfaQrCode,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
